// /anne — Anne Defteri (CR2-01 + CR2-11).
// Üç cümle: pay (TL) → delta (TL + %) → sebep (hedef-dili cümle).
// Preflight bandı üstte (CR2-11): warn/calm geçişi.
// Ev senaryosu kartı (anne hissesi % + TL).
// Audit toggle (kapalı default → fon × anne_oran tablosu).
//
// Veri: /mother/ledger?month=YYYY-MM — month opsiyonel; backend
// currentMonthTR(now)'a düşer.
//
// Synthetic risk: bu sayfa "Anne" varsayımıyla tasarlandı. OP-4 sahne
// simülasyonu (gerçek anne veya proxy) hâlâ açık borç.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMotherLedger,
  ApiError,
  type ApiConfig,
  type MotherLedgerResponse,
} from "../lib/api";
import type { Anomaly } from "../types/api";
import { fmtNum, fmtTLCompact } from "../lib/format";
import AnomalyBadge from "../components/AnomalyBadge";
import PreflightBandi from "../components/PreflightBandi";
import EvSenaryosuKart from "../components/EvSenaryosuKart";
import AnnePayiTablosu from "../components/AnnePayiTablosu";

interface Props {
  cfg: ApiConfig;
  openAnomalies: Anomaly[];
  openAnomaliesCount: number;
}

function formatDeltaPct(n: number): string {
  // 0.009 → "+%0,9"
  const sign = n >= 0 ? "+" : "−";
  const v = Math.abs(n * 100).toFixed(1).replace(".", ",");
  return `${sign}%${v}`;
}

export default function AnneDefteri({
  cfg,
  openAnomalies,
  openAnomaliesCount,
}: Props) {
  const navigate = useNavigate();
  const [auditOpen, setAuditOpen] = useState(false);
  const [ledger, setLedger] = useState<MotherLedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cfg.base) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMotherLedger(cfg)
      .then((out) => {
        if (!cancelled) setLedger(out);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Beklenmedik hata",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cfg]);

  return (
    <main
      className="min-h-screen flex flex-col mx-auto"
      style={{ maxWidth: 430 }}
    >
      {/* Header */}
      <header className="sticky top-0 z-20 bg-ink-900/95 backdrop-blur border-b border-ink-700/60">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <div className="flex items-baseline gap-2 min-w-0">
            <button
              onClick={() => navigate("/")}
              className="text-ink-100 font-semibold tracking-tight text-base hover:text-accent-300 transition"
            >
              AiFon
            </button>
            <span className="text-ink-400 text-sm">·</span>
            <span className="text-ink-300 text-sm">Anne Defteri</span>
          </div>
          <div className="flex items-center gap-1">
            <AnomalyBadge
              count={openAnomaliesCount}
              onClick={() => navigate("/anomaliler")}
            />
          </div>
        </div>
      </header>

      <section className="px-4 pt-5">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate("/")}
            aria-label="Geri"
            className="text-ink-300 hover:text-ink-100 -ml-1 p-1"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="grow min-w-0">
            <h2 className="hedef-dili text-ink-50 text-xl leading-snug">
              Anne Defteri
            </h2>
            <p className="text-ink-300 text-sm mt-0.5">
              {loading ? "yükleniyor…" : (ledger?.period_label ?? "—")}
            </p>
          </div>
        </div>
      </section>

      {error && (
        <section className="px-4 mt-3">
          <div className="rounded-lg bg-loss-500/10 border border-loss-500/30 px-3 py-2 text-[12px] text-loss-400">
            Defter yüklenemedi: {error}
            {!cfg.base && (
              <span className="block mt-1 text-ink-300">
                Settings'ten API base'i ayarla.
              </span>
            )}
          </div>
        </section>
      )}

      {/* Preflight Bandı */}
      <section className="px-4 mt-4">
        <PreflightBandi openAnomaliesCount={openAnomaliesCount} />
      </section>

      {loading && !ledger ? (
        <section className="px-4 mt-6">
          <p className="text-[12px] text-ink-400 italic px-1 py-2">
            Yükleniyor…
          </p>
        </section>
      ) : ledger ? (
        <>
          {/* Üç Cümle Hero */}
          <section className="px-4 mt-6 space-y-6">
            {/* Cümle 1 — Bu ay senin payın */}
            <article>
              <p className="text-[12px] uppercase tracking-wider text-ink-400 font-medium mb-1.5">
                Bu ay senin payın
              </p>
              <p className="anne-hero text-accent-300 text-4xl leading-none font-semibold break-words">
                {fmtTLCompact(ledger.total_share_tl)}
              </p>
              <p className="text-[11px] text-ink-400 mt-2 fon-dili">
                tam: {fmtNum(ledger.total_share_tl_exact)} TL
              </p>
            </article>

            {/* Cümle 2 — Geçen aydan */}
            <article className="pt-5 border-t border-ink-700/60">
              <p className="text-[12px] uppercase tracking-wider text-ink-400 font-medium mb-1.5">
                Geçen aydan
              </p>
              <p
                className={`anne-hero text-2xl leading-none font-medium flex items-baseline gap-2 flex-wrap ${
                  ledger.delta_vs_last_month_tl >= 0
                    ? "text-gain-400"
                    : "text-loss-400"
                }`}
              >
                <span className="whitespace-nowrap">
                  {ledger.delta_vs_last_month_tl >= 0 ? "+" : "−"}
                  {fmtNum(Math.abs(ledger.delta_vs_last_month_tl))} TL
                </span>
                <span
                  className={
                    ledger.delta_vs_last_month_tl >= 0
                      ? "text-gain-500/80 text-base"
                      : "text-loss-500/80 text-base"
                  }
                  aria-hidden="true"
                >
                  {ledger.delta_vs_last_month_tl >= 0 ? "▲" : "▼"}
                </span>
              </p>
              <p className="text-[11px] text-ink-400 mt-2">
                geçen ay başına göre {formatDeltaPct(ledger.delta_pct)}
              </p>
            </article>

            {/* Cümle 3 — Bu artış nereden geldi */}
            <article className="pt-5 border-t border-ink-700/60">
              <p className="text-[12px] uppercase tracking-wider text-ink-400 font-medium mb-1.5">
                Bu artış nereden geldi
              </p>
              <p className="hedef-dili text-ink-100 text-base leading-snug">
                {ledger.reason_sentence}
              </p>
            </article>
          </section>

          {/* Ev senaryosu */}
          <section className="px-4 mt-6">
            <EvSenaryosuKart
              sharePct={ledger.house_share_pct}
              shareTl={ledger.house_share_tl}
              targetTl={ledger.house_target_tl}
            />
          </section>

          {/* Audit toggle */}
          <section className="px-4 mt-6">
            <button
              type="button"
              onClick={() => setAuditOpen((v) => !v)}
              className="w-full flex items-center gap-2 text-[12px] text-ink-300 hover:text-ink-100 py-2"
              aria-expanded={auditOpen}
            >
              <span
                className="caret inline-block transition-transform"
                style={{
                  transform: auditOpen ? "rotate(90deg)" : "rotate(0deg)",
                }}
              >
                ▸
              </span>
              <span>Detaylı görünüm</span>
              <span className="ml-auto text-ink-500 text-[11px]">
                fon × anne_oran × units × gross
              </span>
            </button>

            <div
              className={`audit-drawer ${auditOpen ? "is-open" : "is-closed"}`}
              aria-hidden={!auditOpen}
            >
              <AnnePayiTablosu
                rows={ledger.rows}
                openAnomalies={openAnomalies}
                totalTl={ledger.total_share_tl_exact}
              />
            </div>
          </section>

          <footer className="mt-auto px-4 pb-6 pt-8 text-center text-[11px] text-ink-400">
            AiFon · Anne Defteri · {ledger.period_label} ·{" "}
            <span className="fon-dili text-ink-500">
              {fmtTLCompact(ledger.total_share_tl_exact)}
            </span>
          </footer>
        </>
      ) : (
        <footer className="mt-auto px-4 pb-6 pt-8 text-center text-[11px] text-ink-400">
          AiFon · Anne Defteri
        </footer>
      )}
    </main>
  );
}
